// CRMIntegrations.jsx - Phone Integration UI (Go High Level)
// Allows agents to use their GHL phone number for SMS in Pro IRP
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Phone,
  MessageSquare,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Plug,
  AlertTriangle,
  Shield,
  Zap
} from 'lucide-react';
import {
  getCRMIntegrations,
  connectCRM,
  disconnectCRM
} from '../api';

export default function CRMIntegrations() {
  const { t } = useTranslation();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCRMIntegrations();
      setIntegrations(data.integrations || []);
    } catch (err) {
      console.error('Failed to load integrations:', err);
      setError('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (crmType) => {
    try {
      const data = await connectCRM(crmType);
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err) {
      console.error(`Failed to connect ${crmType}:`, err);
      alert(`Failed to connect. Please try again.`);
    }
  };

  const handleDisconnect = async (integrationId) => {
    if (!window.confirm('Are you sure you want to disconnect Go High Level? You will need to use a different phone system for automations.')) {
      return;
    }

    try {
      await disconnectCRM(integrationId);
      await loadIntegrations();
    } catch (err) {
      console.error('Failed to disconnect:', err);
      alert('Failed to disconnect. Please try again.');
    }
  };

  const ghlIntegration = integrations.find(i => i.crm_type === 'gohighlevel');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-[#FFB800]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-8">
      {/* Section Header */}
      <div className="border-t-2 border-gray-200 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Phone className="text-orange-600" size={28} />
          <h2 className="text-2xl font-extrabold text-[#172A3A]">Phone Integration</h2>
        </div>
        <p className="text-gray-600 mb-6">
          Connect your Go High Level account to use your existing GHL phone number for Pro IRP automations and messaging.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
          <AlertTriangle size={20} />
          {error}
        </div>
      )}

      {/* Go High Level Card */}
      <div className={`bg-white rounded-2xl shadow-lg p-6 border-2 transition-all ${ghlIntegration?.has_access_token ? 'border-green-500' : 'border-gray-200'}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center">
              <span className="text-white text-2xl font-bold">GHL</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#172A3A] flex items-center gap-2">
                Go High Level
                {ghlIntegration?.has_access_token && <CheckCircle2 className="text-green-600" size={20} />}
              </h3>
              <p className="text-gray-600 text-sm">Use your GHL phone for Pro IRP messaging</p>
            </div>
          </div>
          <div>
            {ghlIntegration?.has_access_token ? (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg font-semibold text-sm">
                <CheckCircle2 size={16} />
                Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-semibold text-sm">
                <XCircle size={16} />
                Not Connected
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-start gap-3">
            <MessageSquare className="text-orange-600 mt-1" size={18} />
            <div>
              <div className="font-semibold text-[#172A3A]">Send & Receive SMS</div>
              <div className="text-sm text-gray-600">
                Pro IRP automations send messages through your GHL phone number
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Zap className="text-orange-600 mt-1" size={18} />
            <div>
              <div className="font-semibold text-[#172A3A]">Unified Communication</div>
              <div className="text-sm text-gray-600">
                All messages sync to Pro IRP - no double-texting clients
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Shield className="text-orange-600 mt-1" size={18} />
            <div>
              <div className="font-semibold text-[#172A3A]">Smart Protection</div>
              <div className="text-sm text-gray-600">
                Automations check recent contact history before sending
              </div>
            </div>
          </div>
        </div>

        {ghlIntegration?.has_access_token ? (
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-gray-600">
              {ghlIntegration.phone_number ? (
                <span className="flex items-center gap-2">
                  <Phone size={16} className="text-green-600" />
                  Phone: <span className="font-semibold">{ghlIntegration.phone_number}</span>
                </span>
              ) : (
                <span className="flex items-center gap-2 text-yellow-600">
                  <AlertTriangle size={16} />
                  No phone number found in your GHL location
                </span>
              )}
            </div>
            <button
              onClick={() => handleDisconnect(ghlIntegration.id)}
              className="text-red-600 hover:text-red-700 px-3 py-2 font-semibold transition"
            >
              Disconnect
            </button>
          </div>
        ) : (
          <button
            onClick={() => handleConnect('gohighlevel')}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 font-bold rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
          >
            <Plug size={20} />
            Connect Go High Level
          </button>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
        <div className="flex gap-4">
          <div className="text-orange-600 mt-1">
            <Phone size={24} />
          </div>
          <div>
            <h4 className="font-bold text-orange-900 mb-2">How It Works</h4>
            <ul className="text-sm text-orange-800 space-y-1">
              <li>• Connect your GHL account with one click</li>
              <li>• Pro IRP sends automated messages through your GHL phone</li>
              <li>• Incoming and outgoing messages sync automatically</li>
              <li>• Smart anti-bombardment prevents over-messaging clients</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
